import React, { useState, useRef, useEffect } from 'react';
import { Image, ImageProps } from 'expo-image';
import { ActivityIndicator, View, StyleSheet } from 'react-native';
import { useTheme } from '../contexts/ThemeContext';

interface CachedImageProps extends Omit<ImageProps, 'source'> {
  uri: string;
  contentFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
}

// Global cache to track which images have been loaded (prevents flickering on re-mount)
const loadedImagesCache = new Set<string>();

const CachedImage = React.memo(function CachedImage({ uri, style, ...props }: CachedImageProps) {
  // Check if this image was already loaded before (from home screen, etc.)
  const wasAlreadyLoaded = useRef(loadedImagesCache.has(uri));
  const [loading, setLoading] = useState(!wasAlreadyLoaded.current);
  const [error, setError] = useState(false);
  const { colors } = useTheme();

  if (error) {
    return (
      <View style={[styles.container, style]}>
        <View style={[styles.placeholder, { backgroundColor: colors.surface }]} />
      </View>
    );
  }

  // Extract borderRadius from style to apply to container for overflow: hidden
  const styleArray = Array.isArray(style) ? style : [style];
  const flattenedStyle = StyleSheet.flatten(styleArray);
  const borderRadius = flattenedStyle?.borderRadius;

  // Skip transition animation if image was already loaded (prevents flicker)
  const transitionDuration = wasAlreadyLoaded.current ? 0 : 300;

  return (
    <View style={[styles.container, style]}>
      {loading && (
        <View style={[styles.loaderContainer, { backgroundColor: colors.surface }]}>
          <ActivityIndicator size="small" color={colors.textSecondary} />
        </View>
      )}
      <Image
        {...props}
        source={{ uri }}
        style={[styles.image, style, borderRadius ? { borderRadius } : undefined]}
        contentFit={props.contentFit || 'cover'}
        // Caching configuration - disk and memory caching enabled by default
        cachePolicy="memory-disk"
        // Priority for loading (normal is default, use 'high' for above-the-fold images)
        priority="normal"
        // Transition animation when image loads (skip if already cached)
        transition={transitionDuration}
        onLoadStart={() => {
          // Only show loading if this is a fresh load
          if (!wasAlreadyLoaded.current) {
            setLoading(true);
          }
        }}
        onLoadEnd={() => {
          setLoading(false);
          // Mark this image as loaded for future mounts
          loadedImagesCache.add(uri);
        }}
        onError={() => {
          setLoading(false);
          setError(true);
        }}
      />
    </View>
  );
});

export default CachedImage;

const styles = StyleSheet.create({
  container: {
    position: 'relative',
    overflow: 'hidden',
  },
  image: {
    width: '100%',
    height: '100%',
  },
  loaderContainer: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'center',
    alignItems: 'center',
  },
  placeholder: {
    width: '100%',
    height: '100%',
  },
});
